<template>
  <div class="filter-bar flex flex-col bg-surface-0 dark:bg-surface-900 md:max-w-full">
    <section class="filter-bar--filters-wrapper w-full">
      <div class="grid grid-cols-2 md:grid-cols-6 xl:grid-cols-12 gap-6 w-full">
        <div
          class="filter-bar--search-wrapper col-span-2 md:col-span-3 xl:col-span-2"
        >
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Search</label
          >
          <InputText
            id="search"
            v-model="searchValue"
            class="w-full"
            icon="pi pi-search"
            placeholder="Search"
            size="small"
          />
        </div>

        <div class="filter-bar--event-type-wrapper col-span-1 md:col-span-2">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Event types</label
          >
          <MultiSelect
            id="event-type"
            v-model="eventTypeSelected"
            :options="eventTypeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Event types"
            selectedItemsLabel="Multiple items selected"
            :maxSelectedLabels="1"
            size="small"
          />
        </div>

        <div class="filter-bar--alert-level-wrapper col-span-1 xl:col-span-2">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Tolerance Level</label
          >
          <MultiSelect
            id="alert-level"
            v-model="alertLevelSelected"
            :options="alertLevelOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Tolerance level"
            size="small"
            :maxSelectedLabels="1"
            :showToggleAll="false"
            :showSelectAll="false"
          />
        </div>
        <div
          class="filter-bar--significance-wrapper col-span-2 md:col-span-3 xl:col-span-4"
        >
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Significance</label
          >

          <div class="flex gap-2">
            <InputNumber
              id="significance-min"
              v-model="significanceMin"
              fluid
              :min="actualMinMaxSignificance.min"
              :max="actualMinMaxSignificance.max"
              :showButtons="true"
              mode="currency"
              currency="USD"
              placeholder="Min significance"
              :step="1000"
              size="small"
            />

            <InputNumber
              id="significance-max"
              v-model="significanceMax"
              fluid
              :min="actualMinMaxSignificance.min"
              :max="actualMinMaxSignificance.max"
              :showButtons="true"
              mode="currency"
              currency="USD"
              placeholder="Max significance"
              :step="1000"
              size="small"
            />
          </div>
        </div>
        <div
          class="filter-bar--reset-wrapper col-span-2 flex justify-end items-end gap-3"
        >
          <Button
            label="Reset"
            variant="outlined"
            @click="reset"
            icon="pi pi-times"
            size="small"
          />
          <!-- Slot for custom refresh button, with default fallback -->
          <slot name="refresh-button" :refresh="refreshAlerts">
            <Button
              label="Refresh"
              @click="refreshAlerts"
              icon="pi pi-refresh"
              size="small"
            />
          </slot>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { storeToRefs } from 'pinia'
import MultiSelect from 'primevue/multiselect'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { useFCCAlertStore } from '@/stores/fcc'
import { AlertLevel } from '@/types/fcc'
import { useFrontegg } from '@frontegg/vue'

const { authState } = useFrontegg()
const alertStore = useFCCAlertStore()
const { alerts, filters, actualMinMaxSignificance } = storeToRefs(alertStore)

// Event types - the type of event that triggered the alert
const eventTypeOptions = computed(() =>
  [...filters.value.eventType.options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  ),
)
const eventTypeSelected = computed({
  get: () => filters.value.eventType.selected,
  set: (value: string[]) => {
    alertStore.updateFilters({
      eventType: { selected: value, options: filters.value.eventType.options },
    })
  },
})

// Alert levels - the severity of the alert
const alertLevelOptions = computed(() => filters.value.alertLevel.options)
const alertLevelSelected = computed({
  get: () => filters.value.alertLevel.selected,
  set: (value: AlertLevel[]) => {
    alertStore.updateFilters({
      alertLevel: {
        selected: value,
        options: filters.value.alertLevel.options,
      },
    })
  },
})

// Significance - the estimated cost to the company
const significanceMin = ref<number | null>(
  filters.value.significance.min || actualMinMaxSignificance.value.min || null,
)
const significanceMax = ref<number | null>(
  filters.value.significance.max || actualMinMaxSignificance.value.max || null,
)
const searchValue = ref<string>(filters.value.search.value || '')

// Watch for changes and update store
watch(significanceMin, newValue => {
  alertStore.updateFilters({
    significance: {
      ...filters.value.significance,
      min: newValue || 0,
    },
  })
})

watch(significanceMax, newValue => {
  alertStore.updateFilters({
    significance: {
      ...filters.value.significance,
      max: newValue || filters.value.significance.max,
    },
  })
})

watch(searchValue, newValue => {
  alertStore.updateFilters({ search: { value: newValue } })
})

// Watch store changes to update local refs
watch(
  () => filters.value.significance,
  newSignificance => {
    significanceMin.value = newSignificance.min || null
    significanceMax.value = newSignificance.max || null
  },
  { deep: true },
)

watch(
  () => filters.value.search.value,
  newSearch => {
    searchValue.value = newSearch || ''
  },
)

// Watch for changes in actual min/max significance and update local refs if they're not set
watch(
  actualMinMaxSignificance,
  newMinMax => {
    if (significanceMin.value === null || significanceMin.value === 0) {
      significanceMin.value = newMinMax.min
    }
    if (significanceMax.value === null || significanceMax.value === 1000000) {
      significanceMax.value = newMinMax.max
    }
  },
  { immediate: true },
)

// Watch for changes in alerts from filtering, and update event type options
watch(
  alerts,
  () => {
    const uniqueTypes = Array.from(new Set(alerts.value.map(a => a.EVENT_TYPE)))
    alertStore.updateFilters({
      eventType: {
        selected: filters.value.eventType.selected.filter(s =>
          uniqueTypes.includes(s),
        ),
        options: uniqueTypes
          .map(t => ({
            value: t,
            label: t
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b\w/g, c => c.toUpperCase()),
          }))
          .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          ),
      },
    })
  },
  { immediate: true },
)

// Reset the filters
function reset() {
  alertStore.resetFilters()
  // Reset local refs to actual min/max values
  significanceMin.value = actualMinMaxSignificance.value.min
  significanceMax.value = actualMinMaxSignificance.value.max
  searchValue.value = ''
}

function refreshAlerts() {
  alertStore.fetchAlerts(authState)
}

// Expose refresh function for parent components to use
defineExpose({ refreshAlerts })
</script>
